import type { OwnershipData } from '@account/hooks/useOwnership'
import type { PointsBenefitsData } from '@account/hooks/usePointsBenefits'
import type { AnalyticsGlobalContext } from '../types'
import {
  formatAnalyticsYyyyMmDd,
  formatAnalyticsYyyyMmDdFromMs,
  formatContractMonthDayWithYearGuess,
  inferUseYearBeginMsFromEndMs,
  parseAnalyticsYyyyMmDdToMs,
  parseFlexibleDateMs,
} from './sdrAnalyticsDates'
import { sdrFieldsFromOwnership } from './sdrOwnershipFields'
import { extractFallbackUseYearsFromPointsBenefits, sdrFieldsFromPointsBenefits } from './sdrPointsBenefitsFields'

/** Points-benefits then ownership (ownership overrides `points_owned`); fills use-year gaps when `yearPoints` is thin. @see README */
export function mergePointsAndOwnershipSdr(
  pointsBenefitsData: PointsBenefitsData | null | undefined,
  ownershipData: OwnershipData | null | undefined,
): Partial<AnalyticsGlobalContext> {
  const fromPoints = sdrFieldsFromPointsBenefits(pointsBenefitsData)
  const fromOwnership = sdrFieldsFromOwnership(ownershipData)
  const merged: Partial<AnalyticsGlobalContext> = { ...fromPoints, ...fromOwnership }

  const fallback = extractFallbackUseYearsFromPointsBenefits(pointsBenefitsData)
  if (!merged.current_use_year && fallback.current_use_year) {
    merged.current_use_year = fallback.current_use_year
  }
  if (!merged.end_use_year && fallback.end_use_year) {
    merged.end_use_year = fallback.end_use_year
  }

  const anchorMs = parseFlexibleDateMs(ownershipData?.useByDate) ?? Date.now()

  if (!merged.end_use_year && ownershipData?.useByDate) {
    const y = formatAnalyticsYyyyMmDd(ownershipData.useByDate)
    if (y) {
      merged.end_use_year = y
    }
  }

  if (!merged.end_use_year && ownershipData?.contracts?.length) {
    for (const c of ownershipData.contracts) {
      const y = formatContractMonthDayWithYearGuess(c.useYearEnd, anchorMs)
      if (y) {
        merged.end_use_year = y
        break
      }
    }
  }

  if (!merged.current_use_year && merged.end_use_year) {
    const endMs = parseAnalyticsYyyyMmDdToMs(merged.end_use_year)
    if (endMs !== undefined) {
      merged.current_use_year = formatAnalyticsYyyyMmDdFromMs(inferUseYearBeginMsFromEndMs(endMs))
    }
  }

  return merged
}
